import { useEffect, useState } from "react";

const getMatch = (query: string): boolean =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(query).matches
    : false;

/**
 * Subscribe to a CSS media query and re-render when it flips. A single shared
 * primitive so screens stop hand-rolling `window.innerWidth` checks or pulling
 * in MUI's `useMediaQuery` just to branch on viewport.
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => getMatch(query));

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange(); // sync in case the query changed between render and effect
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
};

/**
 * True below `maxWidth` (defaults to the shared `sm` breakpoint, 600px — the
 * same value used by MUI's theme and tailwind.config's `sm`). Use for the
 * phone layout branch.
 */
export const useIsMobile = (maxWidth = 600): boolean =>
  useMediaQuery(`(max-width: ${maxWidth - 0.02}px)`);

export default useIsMobile;
